import { FC, useEffect, useMemo, useState } from 'react';
import { Location, fetchLocations, syncLocationsFromErp } from '../../api/locations';

const LocationsPanel: FC = () => {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [records, setRecords] = useState<Location[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchLocations({ page, limit, search: debouncedSearch || undefined })
      .then((resp) => {
        if (!active) return;
        setRecords(resp.data);
        setTotal(resp.total);
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Failed to load locations.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, limit, debouncedSearch, refreshCounter]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit || 1)), [total, limit]);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setError(null);
    try {
      await syncLocationsFromErp();
      // trigger refresh even when already on page 1
      setRefreshCounter((c) => c + 1);
      setPage(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync locations.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search locations..."
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
        />

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync from ERP'}
        </button>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Code</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Phone</th>
              <th className="px-5 py-3 font-semibold">Area Code</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-5 py-6 text-center text-slate-400" colSpan={6}>Loading locations…</td></tr>
            ) : error ? (
              <tr><td className="px-5 py-6 text-center text-danger" colSpan={6}>{error}</td></tr>
            ) : records.length === 0 ? (
              <tr><td className="px-5 py-6 text-center text-slate-400" colSpan={6}>No locations found.</td></tr>
            ) : (
              records.map((loc) => (
                <tr key={loc.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-slate-700">{loc.name}</td>
                  <td className="px-5 py-3 text-slate-500">{loc.code ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{loc.email ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{loc.phone ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{loc.areaCode ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{new Date(loc.updatedAt).toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <span>Page {page} of {totalPages} ({limit} / page)</span>
        <div className="flex items-center gap-2">
          <button className="rounded border border-slate-200 px-3 py-1 disabled:opacity-50" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <button className="rounded border border-slate-200 px-3 py-1 disabled:opacity-50" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default LocationsPanel;
